import type { ParsedRuntimeGlb } from './types';

function extractTrackNodeName(trackName: string): string {
  const separatorIndex = trackName.indexOf('.');
  return separatorIndex === -1 ? trackName : trackName.slice(0, separatorIndex);
}

export function analyzeAnimations(runtime: ParsedRuntimeGlb) {
  const animatedCameraNodeNames = new Set<string>();
  const animatedCameraClipNames = new Set<string>();
  const cameraNodeNames = new Set(
    runtime.cameras
      .map((camera) => camera.name)
      .filter((name): name is string => Boolean(name)),
  );
  const sceneObjectNames = new Set<string>();

  runtime.scene?.traverse((object) => {
    if (object.name) {
      sceneObjectNames.add(object.name);
      if ((object as unknown as { isCamera?: boolean }).isCamera) {
        cameraNodeNames.add(object.name);
      }
    }
  });

  const issues = runtime.animations.flatMap((clip) => {
    const clipIssues = [] as Array<{ code: string; severity: 'error'; message: string }>;

    if (clip.tracks.length === 0) {
      clipIssues.push({
        code: 'animation.clip.empty',
        severity: 'error',
        message: `Animation clip "${clip.name || 'Unnamed'}" contains no valid tracks.`,
      });
    }

    clip.tracks.forEach((track) => {
      const nodeName = extractTrackNodeName(track.name);
      if (!sceneObjectNames.has(nodeName)) {
        clipIssues.push({
          code: 'animation.track.missing-node',
          severity: 'error',
          message: `Animation track "${track.name}" references a missing node.`,
        });
      }

      const times = track.times;
      for (let index = 1; index < times.length; index += 1) {
        if (!(times[index] > times[index - 1])) {
          clipIssues.push({
            code: 'animation.track.times.invalid',
            severity: 'error',
            message: `Animation track "${track.name}" contains non-ascending keyframe times.`,
          });
          break;
        }
      }

      for (const value of track.values) {
        if (!Number.isFinite(value)) {
          clipIssues.push({
            code: 'animation.track.values.invalid',
            severity: 'error',
            message: `Animation track "${track.name}" contains non-finite values.`,
          });
          break;
        }
      }

      if (cameraNodeNames.has(nodeName)) {
        animatedCameraNodeNames.add(nodeName);
        animatedCameraClipNames.add(clip.name || 'Unnamed');
      }
    });

    return clipIssues;
  });

  return {
    animatedCameraClipNames: [...animatedCameraClipNames],
    animatedCameraNodeNames: [...animatedCameraNodeNames],
    issues,
  };
}
